/**
 * D9b + Phase F: chat briefing entry point.
 *
 * Renders a tier-gated button that runs a scope-specific briefing
 * query — "Brief me on this client", "Brief me on this matter",
 * "Brief me on this case". Each tier corresponds to a cascade
 * access layer; the runtime's search.client / search.matter /
 * search.case gate enforces that the caller has the right role.
 *
 * ## Phase D (landed D9b)
 *
 * - Button renders the live entry point
 * - Click fired a "Coming in Phase F" stub message
 *
 * ## Phase F (this revision)
 *
 * - Click triggers a federated search via the relevant RPC:
 *     client tier → `search.client`
 *     matter tier → `search.matter`
 *     case tier   → `search.case` (Phase G follow-up wires the
 *                   case-tier button; kept as a stub for now since
 *                   CaseWorkspaceLayout's existing chat handles case
 *                   scope)
 * - Results render in an inline drawer below the button
 * - The briefing query is a fixed summary prompt
 *   ("Resumo das movimentações recentes") — future work replaces
 *   the fixed prompt with a full chat session owned by the scope
 *
 * The inline-results approach is intentionally not a full chat
 * session: it's a drill-down preview that surfaces the most
 * relevant cross-matter/cross-case snippets the assistant would
 * otherwise answer with. A full chat session keyed on the scope is
 * tracked as a post-F follow-up because it requires a new session
 * key type and the chat store's session routing to understand
 * client-level + matter-level keys.
 */

import { useState } from "react";
import { AlertCircle, Loader2, MessageSquare, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCaseLabel } from "@/lib/case-labels";
import { useGatewayStore } from "@/stores/useGatewayStore";
import type { SearchResult } from "@/types/gateway";

/**
 * Phase F ships briefing at the client + matter tiers only. The case
 * tier is handled by CaseWorkspaceLayout's existing in-case chat
 * session via `useChatSession`, so a dedicated case-tier button
 * would duplicate that flow. A case-tier briefing surface (distinct
 * from the free-form per-case chat) is a post-F follow-up. Narrowing
 * the union to client|matter removes dead code paths that the unit
 * tests cannot reach.
 */
export type BriefingTier = "client" | "matter";

interface ChatBriefingButtonProps {
  tier: BriefingTier;
  /** Name of the entity (client name, matter name) for the button copy. */
  label: string;
  /** Used to pick a matterType-aware display label for the matter tier. */
  matterType?: string;
  /** Required scope id for both tiers. */
  clientId?: string;
  /** Required for the matter tier. */
  matterId?: string;
  /** Required for the cascade-access gate on both tiers. */
  userId?: string;
  /** Required for the cascade-access gate on both tiers. */
  orgId?: string;
}

/**
 * The LLM query that drives the briefing search. Stays Portuguese
 * here because the federated-search RPC consumes it as-is; LLM
 * output-language steering is the responsibility of the runtime
 * resolver's `narrow.languageHint` (Phase E of the localization
 * plan). Once Phase E wires that through, the user's locale will
 * govern the response language without the query string changing.
 */
const DEFAULT_QUERY = "Resumo das movimentações recentes";
const RESULT_LIMIT = 8;

type State =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; results: SearchResult[] }
  | { kind: "error"; message: string };

export function ChatBriefingButton({
  tier,
  label,
  matterType,
  clientId,
  matterId,
  userId,
  orgId,
}: ChatBriefingButtonProps) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [state, setState] = useState<State>({ kind: "idle" });
  const matterCaseLabel = useCaseLabel(matterType);

  // Matter tier uses the matterType-aware label ("Briefing for this
  // contract" vs the generic "Matter briefing"); client tier always
  // uses the generic title.
  const tierLabel =
    tier === "matter" && matterType
      ? t("chat.briefing.tierTitleMatterTyped", { matterCase: matterCaseLabel.toLowerCase() })
      : tier === "client"
        ? t("chat.briefing.tierTitleClient")
        : t("chat.briefing.tierTitleMatterDefault");

  // Phase F audit fix: disable the button until identity (userId +
  // orgId) AND scope ids are available. The hook returns null on
  // first render while the auth-exchange promise resolves; without
  // this gate a click in the first ~100ms produces a confusing
  // error banner.
  const scopeReady =
    Boolean(clientId) &&
    Boolean(userId) &&
    Boolean(orgId) &&
    (tier !== "matter" || Boolean(matterId));
  const disabled = state.kind === "loading" || !scopeReady;

  async function runBriefing() {
    if (!rpc || connectionState !== "connected") {
      setState({ kind: "error", message: t("chat.briefing.errorGatewayDisconnected") });
      return;
    }

    // scopeReady guards the click path; this runtime check is a
    // belt-and-suspenders narrowing for TypeScript.
    if (!clientId || !userId || !orgId) {
      setState({
        kind: "error",
        message: t("chat.briefing.errorScopeMissing"),
      });
      return;
    }
    if (tier === "matter" && !matterId) {
      setState({ kind: "error", message: t("chat.briefing.errorMatterIdMissing") });
      return;
    }

    setState({ kind: "loading" });
    try {
      const results =
        tier === "client"
          ? await rpc.searchClient({
              clientId,
              userId,
              orgId,
              query: DEFAULT_QUERY,
              limit: RESULT_LIMIT,
            })
          : await rpc.searchMatter({
              clientId,
              matterId: matterId!,
              userId,
              orgId,
              query: DEFAULT_QUERY,
              limit: RESULT_LIMIT,
            });
      setState({ kind: "loaded", results: results ?? [] });
    } catch (err) {
      setState({ kind: "error", message: (err as Error).message });
    }
  }

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
      data-testid={`chat-briefing-${tier}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950">
          <Sparkles size={20} className="text-blue-600 dark:text-blue-300" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {tierLabel}
          </h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t("chat.briefing.body")} <span className="font-medium">{label}</span> —{" "}
            {t("chat.briefing.bodyContinuation")}
          </p>
          <button
            type="button"
            onClick={runBriefing}
            disabled={disabled}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid={`chat-briefing-${tier}-open`}
          >
            {state.kind === "loading" ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <MessageSquare size={14} />
            )}
            {state.kind === "loading"
              ? t("chat.briefing.buttonLoading")
              : t("chat.briefing.buttonGenerate")}
          </button>

          {state.kind === "loaded" && (
            <div
              className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800"
              data-testid={`chat-briefing-${tier}-results`}
            >
              {state.results.length === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("chat.briefing.emptyResults")}
                </p>
              ) : (
                <>
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("chat.briefing.resultsCount", { count: state.results.length })}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {state.results.map((r) => (
                      <li
                        key={`${r.type}:${r.id}`}
                        className="flex flex-col gap-0.5 text-xs"
                      >
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                          {r.title}
                        </span>
                        <span className="truncate text-slate-500 dark:text-slate-400">
                          {r.snippet}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {state.kind === "error" && (
            <div
              className="mt-3 flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
              data-testid={`chat-briefing-${tier}-error`}
            >
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              {state.message}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
